<template>
  <div
    ref="searchContainer"
    v-click-outside="handleBlur"
    class="search-container"
    @keydown.tab="handleTab"
    @keydown.esc="handleCollapse"
    @keydown.delete="handleDelete"
    @keydown.enter="handleEnter"
    @keydown.prevent.up="handleMoveHighlight(MoveDirection.Up)"
    @keydown.prevent.down="handleMoveHighlight(MoveDirection.Down)"
    @keydown.left="handleMoveHighlight(MoveDirection.Left)"
    @keydown.right="handleMoveHighlight(MoveDirection.Right)"
  >
    <FriedRoundedButton
      v-if="showSearchButton"
      class="search-button"
      data-external="topbar-search-button"
      @click="handleClickSearchButton"
    >
      <template #icon>
        <FriedIcon :size="IconSize.Small" icon="search" />
      </template>
    </FriedRoundedButton>
    <FriedSearchInput
      v-show="showSearchBox"
      ref="searchBox"
      v-model="searchValue"
      class="search-box"
      data-external="topbar-search-input"
      :placeholder="placeholder"
      @update:model-value="handleInput"
      @focus="handleFocus"
    >
      <template #left>
        <FriedIcon v-show="showSearchIcon" :size="IconSize.Small" icon="search" />
      </template>
      <template #right>
        <FriedIconButton
          v-show="showClearSearchButton"
          v-tooltip="{
            content: $t('clear-search'),
            placement: Placement.Bottom,
            delay: { show: 300, hide: 100 },
          }"
          class="clear-search-button"
          data-external="topbar-clear-search-button"
          @keydown.enter.stop
          @click="handleClearInput"
        >
          <FriedIcon icon="close" :size="IconSize.Small" />
        </FriedIconButton>
      </template>
    </FriedSearchInput>
    <TransitionFadeInOut>
      <SearchResult
        v-show="resultExpanded"
        class="search-result"
        :result="searchResults"
        :loading="deferredLoading"
        :highlighted-item="highlightedResultItem"
        @select-contact="goToContact"
        @select-document="goToDocument"
      />
    </TransitionFadeInOut>
  </div>
</template>

<script lang="ts">
import { IconSize, Placement } from '@getaccept/fried-tofu';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import type { Ref } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { useDebounceFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { MoveDirection } from '../types/enums/move-direction';
import type { HighlightedSearchResultItem } from '../types/search-result';
import { SearchResultType } from '../types/enums/search-result-type';
import { SearchHelper } from '../helpers/search.helper';
import { useDeferredLoading } from '../../../common/composables/deferred-loading.composable';
import { useSearchStore } from '../store/search.store.new';
import SearchResult from './SearchResult.vue';

export default defineComponent({
  components: {
    SearchResult,
  },
  props: {
    fixedExpanded: {
      type: Boolean,
    },
    collapseEnabled: {
      type: Boolean,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const highlightedResultItem: Ref<HighlightedSearchResultItem> = ref({
      type: SearchResultType.Documents,
      index: -1,
    });

    const searchStore = useSearchStore();
    const { loading, result } = storeToRefs(searchStore);

    const deferredLoading = useDeferredLoading(loading);

    const searchValue = ref('');
    const inputFocused = ref(false);

    const showSuggestions = computed(() => searchValue.value.length > 1);
    const searchResults = computed(() => {
      const contactsResult = result.value.contacts ?? [];
      const documentsResult = result.value.documents ?? [];
      return { contacts: contactsResult, documents: documentsResult };
    });

    const placeholder = computed(() => {
      if (searchValue.value || inputFocused.value) {
        return '';
      }
      return t('topbar-search-placeholder');
    });

    const showSearchIcon = computed(() => !searchValue.value && !inputFocused.value);

    const inputExpanded = ref(false);
    const showSearchButton = computed(() => {
      if (props.fixedExpanded) {
        return false;
      }
      return props.collapseEnabled && !inputExpanded.value;
    });

    const showSearchBox = computed(() => {
      if (props.fixedExpanded || !props.collapseEnabled) {
        return true;
      }
      return inputExpanded.value;
    });

    const showClearSearchButton = computed(() => !!searchValue.value);

    const debounceSearch = useDebounceFn(
      (searchTerm: string) => searchStore.search({ searchQuery: searchTerm }),
      300
    );

    const resetHighlightedResultItem = () => {
      highlightedResultItem.value = {
        type: SearchResultType.Documents,
        index: -1,
      };
    };

    const resultExpanded = ref(false);

    const handleInput = () => {
      resultExpanded.value = !!searchValue.value;
      resetHighlightedResultItem();
      if (showSuggestions.value) {
        debounceSearch(searchValue.value);
      }
    };

    const handleFocus = () => {
      inputFocused.value = true;
      resultExpanded.value = !!searchValue.value;
    };

    const handleCollapse = () => {
      inputFocused.value = false;
      resultExpanded.value = false;
      if (props.collapseEnabled && !props.fixedExpanded) {
        inputExpanded.value = false;
      }
    };

    const searchContainer = ref<HTMLInputElement>(null);

    const handleBlur = (event: FocusEvent) => {
      if (searchContainer.value.contains(event.target as Node)) {
        return;
      }
      handleCollapse();
    };

    const handleTab = () => {
      setTimeout(() => {
        const currentElement: Element = document.activeElement;
        if (!searchContainer.value.contains(currentElement)) {
          handleCollapse();
        }
      });
    };

    const handleDelete = () => {
      searchStore.clearData();
    };
    const searchBox = ref<HTMLInputElement>(null);

    const router = useRouter();

    const redirectTo = (path: string) => {
      handleCollapse();
      searchBox.value.blur();
      if (props.collapseEnabled) {
        emit('close');
      }
      setTimeout(() => {
        router.push(path);
      });
    };

    const goToDocument = (document: Document) => {
      redirectTo(SearchHelper.getDocumentLink(document));
    };

    const goToContact = (contact: User) => redirectTo(SearchHelper.getContactLink(contact));

    const handleEnter = () => {
      const { type, index }: { type: SearchResultType; index: number } =
        highlightedResultItem.value;
      if (highlightedResultItem.value.index === -1) {
        return;
      }
      switch (type) {
        case SearchResultType.Documents:
          return goToDocument(result.value[type][index]);
        case SearchResultType.Contacts:
          return goToContact(result.value[type][index]);
      }
    };

    const handleClearInput = () => {
      searchValue.value = '';
      searchBox.value.focus();
      if (props.collapseEnabled) {
        handleCollapse();
      }
      handleDelete();
    };

    const handleClickSearchButton = () => {
      inputExpanded.value = true;
      searchBox.value.focus();
    };

    const handleMoveHighlight = (direction: MoveDirection) => {
      highlightedResultItem.value = SearchHelper.moveResultItemHighlight(
        direction,
        highlightedResultItem.value,
        result.value
      );
    };

    return {
      MoveDirection,
      IconSize,
      Placement,
      searchBox,
      searchContainer,
      searchValue,
      inputFocused,
      inputExpanded,
      resultExpanded,
      loading,
      highlightedResultItem,
      placeholder,
      showSearchIcon,
      showSearchButton,
      showSearchBox,
      showClearSearchButton,
      searchResults,
      deferredLoading,
      handleInput,
      handleFocus,
      handleBlur,
      handleTab,
      handleEnter,
      goToContact,
      goToDocument,
      handleClearInput,
      handleClickSearchButton,
      handleMoveHighlight,
      handleDelete,
      debounceSearch,
      resetHighlightedResultItem,
      handleCollapse,
      redirectTo,
    };
  },
});
</script>

<style lang="scss" scoped>
$search-input-height: 2.5rem;

.search-container {
  .search-box {
    width: 100%;
    height: var(--spacing-200);

    :deep(.base-search-box-input) {
      font-size: var(--label-small-font-size);
      line-height: var(--label-small-line-height);
      font-weight: var(--label-small-font-weight);
    }

    .clear-search-button {
      height: 1.7rem;
      width: 1.7rem;
      padding: var(--spacing-50);
      margin-right: var(--spacing-25);
    }
  }

  .search-result {
    border-top: 3px solid var(--gray-98);

    @media only screen and (max-width: $sm) {
      margin-top: calc(#{$search-input-height} + #{var(--spacing-50)} * 2);
      border: 0;
    }
  }
}
</style>
